import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Toolbar,
  Tooltip,
} from "@mui/material";

import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";

import { useDispatch, useSelector } from "react-redux";

import CartIcon from "./CartIcon";
import ProductList from "./ProductList";
import CartHoverPreview from "./CartHoverPreview";

import { logoutUser } from "../features/userSlice";
import { CLEAR_CART } from "../features/productInCartSlice";

export default function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loggedUser = useSelector(
    (state) => state.user.logedUser
  );

  const cart = useSelector(
    (state) => state.cart.items
  );

  const [userMenuAnchor, setUserMenuAnchor] =
    useState(null);

  const [cartAnchor, setCartAnchor] =
    useState(null);

  const [showCartPreview, setShowCartPreview] =
    useState(false);

  const closeCartTimer = useRef(null);

  const isUserMenuOpen = Boolean(userMenuAnchor);

  const handleUserIconClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    dispatch(CLEAR_CART());

    localStorage.removeItem("loggedUser");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("cart");

    handleUserMenuClose();
    navigate("/");
  };

  const openCartPreview = (element) => {
    if (closeCartTimer.current) {
      clearTimeout(closeCartTimer.current);
    }

    setCartAnchor(element);
    setShowCartPreview(true);
  };

  const closeCartPreview = () => {
    closeCartTimer.current = setTimeout(() => {
      setShowCartPreview(false);
      setCartAnchor(null);
    }, 150);
  };

  const handleCartClick = () => {
    setShowCartPreview(false);
    setCartAnchor(null);
    navigate("/cart");
  };

  const handleCartKeyDown = (event) => {
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();
      handleCartClick();
    }

    if (event.key === "Escape") {
      setShowCartPreview(false);
      setCartAnchor(null);
    }
  };

  const userButtonLabel = loggedUser?.name
    ? `פתיחת תפריט המשתמש של ${loggedUser.name}`
    : "פתיחת תפריט המשתמש";

  const cartItemsCount = Array.isArray(cart)
    ? cart.reduce(
      (total, item) =>
        total + Number(item?.amount || 0),
      0
    )
    : 0;

  const cartButtonLabel =
    cartItemsCount > 0
      ? `פתיחת עגלת הקניות, ${cartItemsCount} פריטים`
      : "פתיחת עגלת הקניות, העגלה ריקה";

  return (
    <AppBar
      component="header"
      position="sticky"
      color="transparent"
      elevation={0}
      aria-label="סרגל קטגוריות ופעולות האתר"
      sx={{
        top: 0,
        right: 0,
        left: 0,
        width: "100%",
        bgcolor: "transparent",
        pt: 1,

        zIndex: (theme) =>
          theme.zIndex.appBar + 10,

        "& a:focus-visible, & button:focus-visible": {
          outline: "3px solid #D81B60",
          outlineOffset: "4px",
          borderRadius: "6px",
        },
      }}
    >
      <Container
        maxWidth={false}
        disableGutters
      >
        <Box
          sx={{
            width: "min(1400px, 100%)",
            mx: "auto",
            px: {
              xs: 1,
              sm: 2,
            },
          }}
        >
          <Paper
            elevation={6}
            sx={{
              flex: 1,
              borderRadius: 6,
              bgcolor: "#ffffff",
              overflow: "visible",
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                px: {
                  xs: 1,
                  sm: 2,
                },
                gap: {
                  xs: 1,
                  sm: 2,
                },
                flexWrap: "wrap",
                justifyContent:
                  "space-between",
                minHeight: {
                  xs: 48,
                  sm: 56,
                  md: 64,
                },
                transition:
                  "min-height 0.3s ease",
              }}
            >
              {/* רשימת המוצרים / הניווט */}
              <Box
                component="nav"
                aria-label="ניווט בין קטגוריות ומוצרים"
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  minWidth: 0,

                  "& a:focus-visible, & button:focus-visible":
                  {
                    outline:
                      "3px solid #D81B60",
                    outlineOffset: "3px",
                    borderRadius: "5px",
                  },
                }}
              >
                <ProductList />
              </Box>

              {/* כפתורי משתמש, מנהל ועגלה */}
              <Box
                component="nav"
                aria-label="פעולות משתמש"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {loggedUser?.tipeUser ===
                  "admin" && (
                    <Tooltip
                      title="מעבר לאזור הניהול"
                      arrow
                    >
                      <IconButton
                        component={Link}
                        to="/admin"
                        color="inherit"
                        aria-label="מעבר לאזור הניהול"
                        sx={{
                          color: "inherit",

                          "&:hover": {
                            color: "#111010",
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <AdminPanelSettingsIcon
                          aria-hidden="true"
                          sx={{
                            fontSize: {
                              xs: 30,
                              md: 36,
                            },
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                {loggedUser ? (
                  <>
                    <Tooltip
                      title={
                        loggedUser?.name
                          ? `שלום, ${loggedUser.name}`
                          : "החשבון שלי"
                      }
                      arrow
                    >
                      <IconButton
                        id="user-menu-button"
                        color="inherit"
                        onClick={
                          handleUserIconClick
                        }
                        aria-label={
                          userButtonLabel
                        }
                        aria-controls={
                          isUserMenuOpen
                            ? "user-menu"
                            : undefined
                        }
                        aria-haspopup="menu"
                        aria-expanded={
                          isUserMenuOpen
                            ? "true"
                            : undefined
                        }
                      >
                        <PersonIcon
                          aria-hidden="true"
                          sx={{
                            fontSize: {
                              xs: 30,
                              md: 36,
                            },
                            color: "#58A76B",
                          }}
                        />
                      </IconButton>
                    </Tooltip>

                    <Menu
                      id="user-menu"
                      anchorEl={
                        userMenuAnchor
                      }
                      open={isUserMenuOpen}
                      onClose={
                        handleUserMenuClose
                      }
                      MenuListProps={{
                        "aria-labelledby":
                          "user-menu-button",
                        autoFocusItem:
                          isUserMenuOpen,
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem
                        onClick={
                          handleLogout
                        }
                      >
                        התנתקות
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Tooltip
                    title="התחברות"
                    arrow
                  >
                    <IconButton
                      color="inherit"
                      component={Link}
                      to="/signIn"
                      aria-label="מעבר לעמוד ההתחברות"
                    >
                      <PersonIcon
                        aria-hidden="true"
                        sx={{
                          fontSize: {
                            xs: 30,
                            md: 36,
                          },
                          color: "#58A76B",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}

                {/* עגלת קניות */}
                <Box
                  onMouseEnter={(event) =>
                    openCartPreview(
                      event.currentTarget
                    )
                  }
                  onMouseLeave={
                    closeCartPreview
                  }
                  onFocus={(event) =>
                    openCartPreview(
                      event.currentTarget
                    )
                  }
                  onBlur={(event) => {
                    const nextElement =
                      event.relatedTarget;

                    if (
                      !event.currentTarget.contains(
                        nextElement
                      )
                    ) {
                      closeCartPreview();
                    }
                  }}
                >
                  <Tooltip
                    title="עגלת קניות"
                    arrow
                  >
                    <IconButton
                      id="cart-button"
                      color="inherit"
                      onClick={
                        handleCartClick
                      }
                      onKeyDown={
                        handleCartKeyDown
                      }
                      aria-label={
                        cartButtonLabel
                      }
                      aria-haspopup="dialog"
                      aria-expanded={
                        showCartPreview
                      }
                      aria-controls={
                        showCartPreview
                          ? "cart-preview"
                          : undefined
                      }
                    >
                      <CartIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Popper
                  id="cart-preview"
                  open={showCartPreview}
                  anchorEl={cartAnchor}
                  placement="bottom-start"
                  role="dialog"
                  aria-label="תצוגה מקדימה של עגלת הקניות"
                  modifiers={[
                    {
                      name: "offset",
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ]}
                  sx={{
                    zIndex: 99999,
                  }}
                >
                  <Box
                    onMouseEnter={() => {
                      if (
                        closeCartTimer.current
                      ) {
                        clearTimeout(
                          closeCartTimer.current
                        );
                      }

                      setShowCartPreview(
                        true
                      );
                    }}
                    onMouseLeave={
                      closeCartPreview
                    }
                    onFocus={() => {
                      if (
                        closeCartTimer.current
                      ) {
                        clearTimeout(
                          closeCartTimer.current
                        );
                      }

                      setShowCartPreview(
                        true
                      );
                    }}
                    onBlur={(event) => {
                      const nextElement =
                        event.relatedTarget;

                      if (
                        !event.currentTarget.contains(
                          nextElement
                        )
                      ) {
                        closeCartPreview();
                      }
                    }}
                    sx={{
                      "& a:focus-visible, & button:focus-visible":
                      {
                        outline:
                          "3px solid #D81B60",
                        outlineOffset:
                          "3px",
                        borderRadius:
                          "5px",
                      },
                    }}
                  >
                    <CartHoverPreview
                      cart={cart || []}
                    />
                  </Box>
                </Popper>
              </Box>
            </Toolbar>
          </Paper>
        </Box>
      </Container>
    </AppBar>
  );
}